import React, { createContext, useState, useEffect, useCallback } from "react";
import {
  fetchUnreviewedMembers,
  fetchAllChildPromotions,
  fetchAllAdultPromotions,
  fetchAllUnpublishedBeltPromotions,
  fetchPendingParentRequests,
  fetchPendingInstructorAttendance,
  fetchPendingAnmalningSubmissionsCount,
} from "../utils/api/api";

export const DataContext = createContext();

export const DataProvider = ({ children, userInfo }) => {
  const [adultPromotions, setAdultPromotions] = useState([]);
  const [childPromotions, setChildPromotions] = useState([]);
  const [unpublishedBeltPromotions, setUnpublishedBeltPromotions] = useState(0);
  const [unreviewedMembersCount, setUnreviewedMembersCount] = useState(0);
  const [pendingParentRequestsCount, setPendingParentRequestsCount] = useState(0);
  const [pendingInstructorAttendancesCount, setPendingInstructorAttendancesCount] = useState(0);
  const [pendingAnmalningSubmissionsCount, setPendingAnmalningSubmissionsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const isAdmin = userInfo && userInfo.length > 0 && userInfo[0].isAdmin;
  const isStaff = isAdmin || Boolean(userInfo?.[0]?.isCoach);

  const unreviewedMembers = useCallback(async () => {
    try {
      const unreviewedMembersData = await fetchUnreviewedMembers();
      return unreviewedMembersData;
    } catch (error) {
      console.error("Failed to fetch unreviewed members:", error);
      return [];
    }
  }, []);

  const forceRefresh = useCallback(() => {
    setHasLoaded(false);
    setLastFetchTime(null);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const now = Date.now();
      const CACHE_DURATION = 5 * 60 * 1000;

      if (hasLoaded && lastFetchTime && (now - lastFetchTime) < CACHE_DURATION) {
        setIsLoading(false);
        return;
      }

      if (!isStaff) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const fetches = [];

        if (isAdmin) {
          fetches.push(
            Promise.all([
              unreviewedMembers(),
              fetchAllChildPromotions(),
              fetchAllAdultPromotions(),
              fetchAllUnpublishedBeltPromotions(),
              fetchPendingParentRequests(),
              fetchPendingInstructorAttendance(),
            ]).then(([
              unreviewedMembersData,
              childPromotionsData,
              adultPromotionsData,
              unpublishedBeltPromotionsData,
              pendingParentRequestsData,
              pendingInstructorAttendancesData,
            ]) => {
              const pendingChildPromotions = (childPromotionsData || [])
                .filter((p) => !p.promotionApproved);
              const pendingAdultPromotions = (adultPromotionsData || [])
                .filter((p) => !p.promotionApproved);

              setChildPromotions(pendingChildPromotions.length);
              setAdultPromotions(pendingAdultPromotions.length);

              const unpublishedFiltered = unpublishedBeltPromotionsData || [];
              setUnpublishedBeltPromotions(unpublishedFiltered.length);

              const unreviewedCount = (unreviewedMembersData || []).length;
              setUnreviewedMembersCount(unreviewedCount);

              const pendingParentsCount = (pendingParentRequestsData || []).length;
              setPendingParentRequestsCount(pendingParentsCount);

              const pendingInstructorFiltered = pendingInstructorAttendancesData || [];
              setPendingInstructorAttendancesCount(pendingInstructorFiltered.length);
            })
          );
        }

        fetches.push(
          fetchPendingAnmalningSubmissionsCount()
            .then((data) => setPendingAnmalningSubmissionsCount(data?.count ?? 0))
            .catch((error) => {
              console.error("Failed to fetch pending anmälningar count:", error);
              setPendingAnmalningSubmissionsCount(0);
            })
        );

        await Promise.all(fetches);

        setHasLoaded(true);
        setLastFetchTime(now);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userInfo, isAdmin, isStaff, unreviewedMembers, hasLoaded, lastFetchTime]);

  return (
    <DataContext.Provider
      value={{
        adultPromotions,
        childPromotions,
        unpublishedBeltPromotions,
        unreviewedMembersCount,
        pendingParentRequestsCount,
        pendingInstructorAttendancesCount,
        pendingAnmalningSubmissionsCount,
        isLoading,
        unreviewedMembers,
        forceRefresh,
        hasLoaded,
        lastFetchTime,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
